<template>
  <div class="min-h-screen bg-dark-900">
    <!-- Navigation -->
    <nav class="bg-dark-800 border-b border-white/10 px-6 py-4">
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <div class="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <QrCodeIcon class="w-6 h-6 text-white" />
          </div>
          <span class="text-xl font-bold text-gradient">Digital Menu AI</span>
        </div>
        <div class="flex items-center space-x-4">
          <span class="text-gray-300">Welcome, {{ user?.firstName }}</span>
          <button @click="logout" class="text-gray-400 hover:text-white transition-colors">
            <LogOutIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </nav>

    <div class="max-w-7xl mx-auto px-6 py-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-gradient mb-2">Dashboard</h1>
        <p class="text-gray-300">Manage your restaurant's digital menu</p>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div v-for="stat in stats" :key="stat.label" class="card-glass p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-400">{{ stat.label }}</p>
              <p class="text-2xl font-bold text-white">{{ stat.value }}</p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <component :is="stat.icon" class="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Locations -->
        <div class="lg:col-span-2">
          <div class="card-glass p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-white">Your Locations</h2>
              <button @click="showAddLocationModal = true" class="btn-primary">
                <PlusIcon class="w-4 h-4 mr-2" />
                Add Location
              </button>
            </div>
            
            <div v-if="locations.length === 0" class="text-center py-12">
              <MapPinIcon class="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 class="text-xl font-semibold text-gray-300 mb-2">No locations yet</h3>
              <p class="text-gray-400 mb-6">Add your first restaurant location to get started</p>
              <button @click="showAddLocationModal = true" class="btn-primary">
                Add Your First Location
              </button>
            </div>
            
            <div v-else class="space-y-4">
              <div v-for="location in locations" :key="location.Id" class="bg-dark-700 rounded-xl p-6 hover:bg-dark-600 transition-colors">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-xl font-semibold text-white mb-1">{{ location.Name }}</h3>
                    <p class="text-gray-400">{{ location.Address }}</p>
                    <p v-if="getMenuName(location.ActiveMenuId)" class="text-blue-400 text-sm mt-1">
                      Menu: {{ getMenuName(location.ActiveMenuId) }}
                    </p>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button @click="generateQRCode(location)" class="btn-secondary text-sm px-4 py-2">
                      <QrCodeIcon class="w-4 h-4 mr-1" />
                      QR Code
                    </button>
                    <button @click="showQRPdfModal(location)" class="btn-secondary text-sm px-4 py-2">
                      <FileTextIcon class="w-4 h-4 mr-1" />
                      QR PDF
                    </button>
                    <button @click="editLocation(location)" class="text-gray-400 hover:text-white">
                      <EditIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="space-y-6">
          <div class="card-glass p-6">
            <h3 class="text-xl font-bold text-white mb-4">Quick Actions</h3>
            <div class="space-y-3">
              <router-link to="/menu-management" class="w-full btn-secondary text-left justify-start flex items-center">
                <UtensilsIcon class="w-4 h-4 mr-3" />
                Manage Menu Items
              </router-link>
              <router-link to="/category-management" class="w-full btn-secondary text-left justify-start flex items-center">
                <FoldersIcon class="w-4 h-4 mr-3" />
                Manage Categories
              </router-link>
              <button class="w-full btn-secondary text-left justify-start">
                <BarChart3Icon class="w-4 h-4 mr-3" />
                View Analytics
              </button>
              <button class="w-full btn-secondary text-left justify-start">
                <SettingsIcon class="w-4 h-4 mr-3" />
                Settings
              </button>
            </div>
          </div>

          <div class="card-glass p-6">
            <h3 class="text-xl font-bold text-white mb-4">Recent Activity</h3>
            <div class="space-y-3">
              <div class="text-sm text-gray-400">
                <p>Menu updated for Bella Vista</p>
                <span class="text-xs text-gray-500">2 hours ago</span>
              </div>
              <div class="text-sm text-gray-400">
                <p>New QR code generated</p>
                <span class="text-xs text-gray-500">1 day ago</span>
              </div>
              <div class="text-sm text-gray-400">
                <p>Location added: Downtown Branch</p>
                <span class="text-xs text-gray-500">3 days ago</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Location Modal -->
    <div v-if="showAddLocationModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
      <div class="card-glass p-8 w-full max-w-md">
        <h3 class="text-2xl font-bold text-white mb-6">Add New Location</h3>
        <form @submit.prevent="addLocation" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Location Name</label>
            <input
              v-model="newLocation.name"
              type="text"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="Downtown Branch"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Address</label>
            <input
              v-model="newLocation.address"
              type="text"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="123 Main St, City, State"
            >
          </div>
          <div class="flex space-x-4 pt-4">
            <button type="submit" class="btn-primary flex-1">Add Location</button>
            <button type="button" @click="showAddLocationModal = false" class="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div v-if="showQRModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
      <div class="card-glass p-8 w-full max-w-md text-center">
        <h3 class="text-2xl font-bold text-white mb-6">QR Code for {{ selectedLocation?.Name }}</h3>
        
        <div v-if="qrCodeLoading" class="flex justify-center items-center h-64">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
        
        <div v-else-if="qrCodeData" class="space-y-4">
          <div class="bg-white p-4 rounded-xl inline-block">
            <img :src="qrCodeData.QRCodeUrl" :alt="`QR Code for ${selectedLocation?.Name}`" class="w-48 h-48 mx-auto" />
          </div>
          
          <div class="text-left bg-dark-700 p-4 rounded-xl">
            <p class="text-sm text-gray-400 mb-2">Menu URL:</p>
            <p class="text-sm text-white break-all font-mono">{{ qrCodeData.MenuUrl }}</p>
          </div>
          
          <div class="flex space-x-3">
            <button @click="downloadQRCode" class="btn-primary flex-1">
              <DownloadIcon class="w-4 h-4 mr-2" />
              Download
            </button>
            <button @click="copyMenuUrl" class="btn-secondary flex-1">
              <CopyIcon class="w-4 h-4 mr-2" />
              Copy URL
            </button>
          </div>
        </div>
        
        <div v-else class="text-center text-red-400">
          <p>Failed to load QR code</p>
        </div>
        
        <button @click="closeQRModal" class="btn-secondary w-full mt-4">Close</button>
      </div>
    </div>

    <!-- Edit Location Modal -->
    <div v-if="showEditLocationModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
      <div class="card-glass p-8 w-full max-w-lg">
        <h3 class="text-2xl font-bold text-white mb-6">Edit Location</h3>
        <form @submit.prevent="updateLocation" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Location Name</label>
            <input
              v-model="editLocationForm.name"
              type="text"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="Downtown Branch"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Address</label>
            <input
              v-model="editLocationForm.address"
              type="text"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="123 Main St, City, State"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Phone</label>
            <input
              v-model="editLocationForm.phone"
              type="tel"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="(555) 123-4567"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              v-model="editLocationForm.email"
              type="email"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="location@restaurant.com"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Opening Hours</label>
            <input
              v-model="editLocationForm.openingHours"
              type="text"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="Mon-Sun: 9AM-10PM"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Active Menu</label>
            <select
              v-model="editLocationForm.activeMenuId"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
            >
              <option value="">No menu selected</option>
              <option v-for="menu in menus" :key="menu.id" :value="menu.id">
                {{ menu.name }}
              </option>
            </select>
          </div>
          <div class="flex space-x-3">
            <button
              type="button"
              @click="closeEditLocationModal"
              class="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 px-6 rounded-xl font-medium transition-colors border border-white/20"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 px-6 rounded-xl font-medium transition-colors"
            >
              Update Location
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- QR Code PDF Modal -->
    <div v-if="showQRPdfModalFlag" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
      <div class="card-glass p-8 w-full max-w-md">
        <h3 class="text-2xl font-bold text-white mb-6">Generate QR Codes PDF</h3>
        <p class="text-gray-300 mb-6">Create a PDF with multiple QR codes for {{ selectedLocationForPdf?.Name }}</p>
        
        <form @submit.prevent="generateQRCodesPdf" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Number of QR Codes</label>
            <input
              v-model.number="qrPdfForm.numberOfQRCodes"
              type="number"
              min="1"
              max="100"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              placeholder="e.g., 20 for 20 tables"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">QR Codes per Page</label>
            <select
              v-model.number="qrPdfForm.qrCodesPerPage"
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
            >
              <option :value="1">1 per page</option>
              <option :value="2">2 per page</option>
              <option :value="3">3 per page</option>
              <option :value="4">4 per page</option>
              <option :value="6">6 per page</option>
              <option :value="8">8 per page</option>
              <option :value="9">9 per page</option>
              <option :value="12">12 per page</option>
              <option :value="16">16 per page</option>
            </select>
          </div>
          
          <div class="bg-dark-700 p-4 rounded-xl text-sm text-gray-300">
            <p><strong>Preview:</strong></p>
            <p>{{ qrPdfForm.numberOfQRCodes }} QR codes</p>
            <p>{{ qrPdfForm.qrCodesPerPage }} codes per page</p>
            <p>≈ {{ Math.ceil(qrPdfForm.numberOfQRCodes / qrPdfForm.qrCodesPerPage) }} page(s)</p>
          </div>
          
          <div class="flex space-x-3 pt-4">
            <button 
              type="button" 
              @click="closeQRPdfModal" 
              class="flex-1 btn-secondary"
              :disabled="qrPdfLoading"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              class="flex-1 btn-primary"
              :disabled="qrPdfLoading"
            >
              <div v-if="qrPdfLoading" class="flex items-center justify-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </div>
              <div v-else class="flex items-center justify-center">
                <FileTextIcon class="w-4 h-4 mr-2" />
                Download PDF
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  QrCodeIcon, 
  LogOutIcon, 
  PlusIcon, 
  MapPinIcon, 
  EditIcon, 
  FoldersIcon,
  BarChart3Icon, 
  SettingsIcon,
  UsersIcon,
  TrendingUpIcon,
  EyeIcon,
  DownloadIcon,
  CopyIcon,
  UtensilsIcon,
  FileTextIcon
} from 'lucide-vue-next'
import { apiService } from '../services/api'
import { useToast } from '@/composables/useToast'

export default {
  name: 'Dashboard',
  components: {
    QrCodeIcon,
    LogOutIcon,
    PlusIcon,
    MapPinIcon,
    EditIcon,
    FoldersIcon,
    BarChart3Icon,
    SettingsIcon,
    UsersIcon,
    TrendingUpIcon,
    EyeIcon,
    DownloadIcon,
    CopyIcon,
    UtensilsIcon,
    FileTextIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      user: null,
      locations: [],
      menus: [],
      menuItems: [],
      showAddLocationModal: false,
      showEditLocationModal: false,
      showQRModal: false,
      qrCodeLoading: false,
      qrCodeData: null,
      selectedLocation: null,
      editingLocation: null,
      newLocation: {
        name: '',
        address: ''
      },
      editLocationForm: {
        name: '',
        address: '',
        phone: '',
        email: '',
        openingHours: '',
        activeMenuId: ''
      },
      showQRPdfModalFlag: false,
      selectedLocationForPdf: null,
      qrPdfLoading: false,
      qrPdfForm: {
        numberOfQRCodes: 20,
        qrCodesPerPage: 4
      }
    }
  },
  computed: {
    stats() {
      const totalViews = this.menus.reduce((sum, menu) => sum + (menu.viewCount || 0), 0)
      const activeMenuItems = this.menuItems.filter(item => item.isAvailable).length
      
      return [
        { label: 'Total Locations', value: this.locations.length.toString(), icon: 'MapPinIcon' },
        { label: 'Menu Views', value: totalViews.toLocaleString(), icon: 'EyeIcon' },
        { label: 'Active Menu Items', value: activeMenuItems.toString(), icon: 'UtensilsIcon' },
        { label: 'Active Menus', value: this.menus.filter(m => m.isActive).length.toString(), icon: 'TrendingUpIcon' }
      ]
    }
  },
  async mounted() {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        this.user = JSON.parse(userStr)
      } catch (error) {
        console.error('Failed to parse user data:', error)
        this.user = null
      }
    }
    await Promise.all([
      this.loadLocations(),
      this.loadMenus()
    ])
    // Load menu items after menus are loaded
    await this.loadMenuItems()
  },
  methods: {
    async loadLocations() {
      try {
        this.locations = await apiService.getLocations()
        console.log('Loaded locations:', this.locations)
      } catch (error) {
        console.error('Failed to load locations:', error)
        this.toast.error('Loading Failed', 'Unable to load locations: ' + error.message)
      }
    },
    
    async loadMenus() {
      try {
        this.menus = await apiService.getMenus()
        console.log('Loaded menus:', this.menus)
      } catch (error) {
        console.error('Failed to load menus:', error)
        // Don't show error for menus, it's not critical for dashboard
      }
    },

    async loadMenuItems() {
      try {
        // Load menu items for all menus
        this.menuItems = []
        for (const menu of this.menus) {
          try {
            const items = await apiService.getMenuItems(menu.id)
            this.menuItems.push(...items)
          } catch (error) {
            console.error(`Failed to load items for menu ${menu.id}:`, error)
          }
        }
        console.log('Loaded menu items:', this.menuItems)
      } catch (error) {
        console.error('Failed to load menu items:', error)
        // Don't show error for menu items, it's not critical for dashboard
      }
    },
    
    getMenuName(activeMenuId) {
      if (!activeMenuId || !this.menus || this.menus.length === 0) {
        return null
      }
      const menu = this.menus.find(menu => menu.id === activeMenuId)
      return menu ? menu.name : null
    },
    
    async addLocation() {
      try {
        const location = await apiService.createLocation(this.newLocation)
        this.locations.push(location)
        this.showAddLocationModal = false
        this.newLocation = { name: '', address: '' }
      } catch (error) {
        console.error('Failed to add location:', error)
        this.toast.error('Location Creation Failed', 'Unable to add location: ' + error.message)
      }
    },
    async generateQRCode(location) {
      this.selectedLocation = location
      this.showQRModal = true
      this.qrCodeLoading = true
      this.qrCodeData = null
      
      try {
        const qrCodeData = await apiService.generateQRCode(location.Id)
        this.qrCodeData = qrCodeData
      } catch (error) {
        console.error('Failed to generate QR code:', error)
        this.toast.error('QR Code Generation Failed', 'Unable to generate QR code: ' + error.message)
      } finally {
        this.qrCodeLoading = false
      }
    },
    editLocation(location) {
      this.editingLocation = location
      this.editLocationForm = {
        name: location.Name || '',
        address: location.Address || '',
        phone: location.Phone || '',
        email: location.Email || '',
        openingHours: location.OpeningHours || '',
        activeMenuId: location.ActiveMenuId || ''
      }
      this.showEditLocationModal = true
    },
    
    async updateLocation() {
      try {
        const updatedLocation = await apiService.updateLocation(this.editingLocation.Id, this.editLocationForm)
        
        // Update the location in the list
        const index = this.locations.findIndex(l => l.Id === this.editingLocation.Id)
        if (index > -1) {
          this.locations[index] = { ...this.locations[index], ...updatedLocation }
        }
        
        this.toast.success('Location Updated', 'Location details updated successfully')
        this.closeEditLocationModal()
      } catch (error) {
        console.error('Failed to update location:', error)
        this.toast.error('Update Failed', 'Unable to update location: ' + error.message)
      }
    },
    
    closeEditLocationModal() {
      this.showEditLocationModal = false
      this.editingLocation = null
      this.editLocationForm = {
        name: '',
        address: '',
        phone: '',
        email: '',
        openingHours: '',
        activeMenuId: ''
      }
    },
    closeQRModal() {
      this.showQRModal = false
      this.qrCodeData = null
      this.selectedLocation = null
      this.qrCodeLoading = false
    },
    async downloadQRCode() {
      if (!this.qrCodeData?.QRCodeUrl) return
      
      try {
        // Create a temporary link element to download the QR code
        const link = document.createElement('a')
        link.href = this.qrCodeData.QRCodeUrl
        link.download = `qr-code-${this.selectedLocation?.Name || 'location'}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Failed to download QR code:', error)
        this.toast.error('Download Failed', 'Unable to download QR code')
      }
    },
    async copyMenuUrl() {
      if (!this.qrCodeData?.MenuUrl) return
      
      try {
        await navigator.clipboard.writeText(this.qrCodeData.MenuUrl)
        this.toast.success('Copied!', 'Menu URL copied to clipboard successfully')
      } catch (error) {
        console.error('Failed to copy URL:', error)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = this.qrCodeData.MenuUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.toast.success('Copied!', 'Menu URL copied to clipboard successfully')
      }
    },
    showQRPdfModal(location) {
      this.selectedLocationForPdf = location
      this.showQRPdfModalFlag = true
    },
    closeQRPdfModal() {
      this.showQRPdfModalFlag = false
      this.selectedLocationForPdf = null
      this.qrPdfForm = {
        numberOfQRCodes: 20,
        qrCodesPerPage: 4
      }
    },
    async generateQRCodesPdf() {
      this.qrPdfLoading = true
      
      try {
        const blob = await apiService.generateQRCodesPdf(
          this.selectedLocationForPdf.Id,
          this.qrPdfForm.numberOfQRCodes,
          this.qrPdfForm.qrCodesPerPage
        )
        
        // Create a download link
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.selectedLocationForPdf.Name}-QR-Codes.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.toast.success('PDF Generated!', 'QR codes PDF downloaded successfully')
        this.closeQRPdfModal()
      } catch (error) {
        console.error('Failed to generate QR codes PDF:', error)
        this.toast.error('PDF Generation Failed', 'Unable to generate QR codes PDF: ' + error.message)
      } finally {
        this.qrPdfLoading = false
      }
    },
    logout() {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      this.$router.push('/')
    }
  }
}
</script>
